import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { fetchMapping, updateMapping } from '@src/api/actions/dashboard/dataIntegrations';

const TYPE_LABELS = {
  email: 'Automated Email',
  sftp: 'SFTP',
  sharepoint: 'SharePoint',
  api: 'API',
  google_sheet: 'Google Sheet',
  manual: 'Manual Upload',
};

// Shift a "HH:MM" string by N minutes, wrapping at midnight. Used so the
// Email flow can surface the send time to the user while we store the
// actual polling time (send + 15 min) on the mapping.
function shiftHHMM(hhmm, deltaMinutes) {
  if (!hhmm) return '';
  const [hStr, mStr] = String(hhmm).split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const total = ((h * 60 + m + deltaMinutes) % (24 * 60) + 24 * 60) % (24 * 60);
  const ph = String(Math.floor(total / 60)).padStart(2, '0');
  const pm = String(total % 60).padStart(2, '0');
  return `${ph}:${pm}`;
}

// API mapping's `transform_params` carries auth_type + related fields. We
// need to read/write these through the dialog's form state.
const API_AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api_key', label: 'API Key' },
  { value: 'basic', label: 'Basic Auth' },
];

export default function DataIntegrationEditDialog({ open, mapping, onClose, onSaved }) {
  const type = mapping?.integration_type;
  const mappingId = mapping?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});

  // Hydrate the form from the full mapping record when the dialog opens.
  useEffect(() => {
    if (!open || !type || !mappingId) return undefined;
    if (type === 'manual') return undefined; // manual has its own flow
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMapping(type, mappingId)
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        // Display run_time for email as the send time (actual run_time - 15).
        const rawRunTime = (d.run_time || '').slice(0, 5);
        const displayRunTime = type === 'email' ? shiftHHMM(rawRunTime, -15) : rawRunTime;
        const tp = d.transform_params || {};
        setForm({
          enabled: d.enabled !== false,
          schedule_type: d.schedule_type || 'daily',
          run_time: displayRunTime || '06:00',
          email_sender: d.email_sender || '',
          subject: d.subject || '',
          filename_substring: d.filename_substring || '',
          // SFTP
          hostname: d.hostname || '',
          port: d.port || 22,
          username: d.username || '',
          password: '', // never round-tripped from the server (write-only)
          directory: d.directory || '',
          // API
          api_endpoint: d.api_endpoint || '',
          api_key: '', // write-only
          auth_type: tp.auth_type || 'none',
          api_key_header: tp.api_key_header || 'X-API-Key',
          basic_username: tp.basic_username || '',
          // A body template with no stored method is a pre-wizard POST mapping
          // (mirrors process_api_mappings._resolve_method).
          method: (
            tp.method ||
            (d.json_body_template && Object.keys(d.json_body_template).length
              ? 'POST'
              : 'GET')
          ).toUpperCase(),
          // Google Sheet
          spreadsheet_id: d.spreadsheet_id || '',
          worksheet_range: d.worksheet_range || '',
          // SharePoint
          site_url: d.site_url || '',
          folder_path: d.folder_path || '',
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || err?.message || 'Failed to load mapping.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, type, mappingId]);

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Compute polling time live so the user sees the +15-min add.
  const pollingHint = useMemo(() => {
    if (type !== 'email') return null;
    return shiftHHMM(form.run_time, 15);
  }, [type, form.run_time]);

  const handleSave = async () => {
    if (!type || !mappingId) return;
    setSaving(true);
    setError(null);
    try {
      // Shared payload fields
      const payload = {
        enabled: !!form.enabled,
        schedule_type: form.schedule_type,
        // Email: shift send time forward 15 min to land on the polling time
        run_time: type === 'email'
          ? shiftHHMM(form.run_time, 15)
          : form.run_time,
      };

      if (type === 'email') {
        payload.email_sender = form.email_sender;
        payload.subject = form.subject;
        payload.filename_substring = form.filename_substring || '';
      } else if (type === 'sftp') {
        payload.hostname = form.hostname;
        payload.port = Number(form.port) || 22;
        payload.username = form.username;
        payload.directory = form.directory;
        if (form.password) payload.password = form.password; // only if changed
      } else if (type === 'api') {
        payload.api_endpoint = form.api_endpoint;
        const nextTransform = {};
        if (form.method && form.method !== 'GET') nextTransform.method = form.method;
        if (form.auth_type && form.auth_type !== 'none') {
          nextTransform.auth_type = form.auth_type;
          if (form.auth_type === 'api_key' && form.api_key_header) {
            nextTransform.api_key_header = form.api_key_header;
          }
          if (form.auth_type === 'basic' && form.basic_username) {
            nextTransform.basic_username = form.basic_username;
          }
        }
        if (Object.keys(nextTransform).length) payload.transform_params = nextTransform;
        if (form.api_key) payload.api_key = form.api_key; // only if changed
      } else if (type === 'google_sheet') {
        payload.spreadsheet_id = form.spreadsheet_id;
        payload.worksheet_range = form.worksheet_range || '';
      } else if (type === 'sharepoint') {
        payload.site_url = form.site_url;
        payload.folder_path = form.folder_path;
        payload.filename_substring = form.filename_substring || '';
      }

      await updateMapping(type, mappingId, payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || (typeof err?.response?.data === 'object' ? JSON.stringify(err.response.data) : null)
        || err?.message
        || 'Failed to save.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Edit integration
        <Typography variant="body2" color="text.secondary">
          {TYPE_LABELS[type] || type} — {mapping?.client_name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={form.schedule_type || 'daily'}
                  onChange={update('schedule_type')}
                  label="Frequency"
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="hourly">Every Hour</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={type === 'email' ? 'When does your CRM send?' : 'Run Time'}
                type="time"
                value={form.run_time || ''}
                onChange={update('run_time')}
                InputLabelProps={{ shrink: true }}
                helperText={
                  type === 'email' && pollingHint
                    ? `We'll check at ${pollingHint} (15 min later)`
                    : type === 'email'
                      ? "We'll check 15 minutes after your CRM sends"
                      : 'When should we run'
                }
                sx={{ flex: 1 }}
              />
            </Stack>

            {type === 'email' && (
              <>
                <Divider>Email details</Divider>
                <TextField
                  label="Sender Email Address"
                  value={form.email_sender || ''}
                  onChange={update('email_sender')}
                  helperText="The 'From' address on the automated email. Copy from a real email — typos stop imports."
                />
                <TextField
                  label="Subject (substring match)"
                  value={form.subject || ''}
                  onChange={update('subject')}
                  helperText="Emails whose subject contains this string will match. Copy-paste from a real email."
                />
                <TextField
                  label="Attachment filename contains (optional)"
                  value={form.filename_substring || ''}
                  onChange={update('filename_substring')}
                />
              </>
            )}

            {type === 'sftp' && (
              <>
                <Divider>SFTP credentials</Divider>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Host"
                    value={form.hostname || ''}
                    onChange={update('hostname')}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label="Port"
                    type="number"
                    value={form.port || 22}
                    onChange={update('port')}
                    sx={{ width: 100 }}
                  />
                </Stack>
                <TextField
                  label="Username"
                  value={form.username || ''}
                  onChange={update('username')}
                />
                <TextField
                  label="Password"
                  type="password"
                  placeholder="Leave blank to keep current"
                  value={form.password || ''}
                  onChange={update('password')}
                  helperText="Leave blank to keep the existing password. Stored encrypted."
                />
                <TextField
                  label="Directory"
                  value={form.directory || ''}
                  onChange={update('directory')}
                />
              </>
            )}

            {type === 'api' && (
              <>
                <Divider>API configuration</Divider>
                <TextField
                  label="API Endpoint URL"
                  value={form.api_endpoint || ''}
                  onChange={update('api_endpoint')}
                />
                <Stack direction="row" spacing={2}>
                  <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel>HTTP Method</InputLabel>
                    <Select
                      value={form.method || 'GET'}
                      onChange={update('method')}
                      label="HTTP Method"
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Authentication</InputLabel>
                    <Select
                      value={form.auth_type || 'none'}
                      onChange={update('auth_type')}
                      label="Authentication"
                    >
                      {API_AUTH_TYPES.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                {form.auth_type !== 'none' && (
                  <TextField
                    label={form.auth_type === 'bearer' ? 'Bearer Token' : 'API Key / Password'}
                    type="password"
                    placeholder="Leave blank to keep current"
                    value={form.api_key || ''}
                    onChange={update('api_key')}
                    helperText="Leave blank to keep the existing value. Stored encrypted."
                  />
                )}
                {form.auth_type === 'api_key' && (
                  <TextField
                    label="Header Name"
                    value={form.api_key_header || ''}
                    onChange={update('api_key_header')}
                  />
                )}
                {form.auth_type === 'basic' && (
                  <TextField
                    label="Username"
                    value={form.basic_username || ''}
                    onChange={update('basic_username')}
                  />
                )}
              </>
            )}

            {type === 'sharepoint' && (
              <>
                <Divider>SharePoint folder</Divider>
                <TextField
                  label="SharePoint Site URL"
                  value={form.site_url || ''}
                  onChange={update('site_url')}
                  helperText="e.g. https://tenant.sharepoint.com/sites/Marketing-External"
                />
                <TextField
                  label="Folder Path (server-relative)"
                  value={form.folder_path || ''}
                  onChange={update('folder_path')}
                  helperText="Starts with /sites/… and includes 'Shared Documents'."
                />
                <TextField
                  label="Filename contains"
                  value={form.filename_substring || ''}
                  onChange={update('filename_substring')}
                  helperText="Newest file whose name contains this is pulled."
                />
                <Alert severity="info">
                  To (re)connect the login session, use the Connect button on the
                  Data Integrations list.
                </Alert>
              </>
            )}

            {type === 'google_sheet' && (
              <>
                <Divider>Google Sheet</Divider>
                <TextField
                  label="Google Sheet URL or ID"
                  value={form.spreadsheet_id || ''}
                  onChange={update('spreadsheet_id')}
                />
                <TextField
                  label="Sheet & Range"
                  value={form.worksheet_range || ''}
                  onChange={update('worksheet_range')}
                  placeholder="Sheet1!A:Z"
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
